import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { BlockShell } from "./BlockShell";
import { renderInline } from "./StoryChapterBlock";
import { AddToCalendar } from "../ShowsCalendarSync";
import { usePageOwner } from "../../hooks/usePageOwner";
import { groupResidencies } from "../../pages/BandTourPage";

const API = process.env.REACT_APP_BACKEND_URL;

const when = (d, t) => {
  const dt = new Date(`${d}T12:00:00`);
  const day = Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return t ? `${day} · ${t}` : day;
};

// Owner-only strip: verified artists drop a flyer on /my-tour and the show lands here, on the calendar and the map.
export const OwnerShowsCta = ({ slug, accent, compact = false }) => {
  const { isOwner, verified } = usePageOwner(slug);
  if (!isOwner) return null;
  return (
    <Link to="/my-tour" className={`flex items-center justify-center gap-2 rounded-full text-white font-extrabold no-underline hover:opacity-90 ${compact ? "px-4 py-2 text-sm" : "w-full min-h-[56px] px-6 py-3.5 text-base"}`} style={{ backgroundColor: accent }} data-testid="owner-add-shows-cta">
      <Icons.ImagePlus className="h-5 w-5" /> Add shows — just drop a flyer{verified ? "" : " (verify your email first)"}
    </Link>
  );
};

// An artist's upcoming shows (from their tour stops / calendar), with subscribe + owner "drop a flyer" CTA.
export default function ArtistShowsBlock({ block }) {
  const cfg = block.config || {};
  const accent = cfg.accent || "#2E5C55";
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!cfg.artist_slug) return;
    axios.get(`${API}/api/artists/${cfg.artist_slug}/shows`).then((r) => setData(r.data)).catch(() => setData({ upcoming_dates: [], past_dates: [] }));
  }, [cfg.artist_slug]);
  const upcoming = groupResidencies(data?.upcoming_dates || []).slice(0, cfg.limit || 12);
  const past = cfg.show_past ? (data?.past_dates || []).slice(-3).reverse() : [];
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" id={cfg.anchor || "shows"} data-testid="fest-artist-shows">
      <BlockShell block={block} accent={accent}>
        {cfg.intro && <p className="text-sm text-[#5C5C5C] mb-4">{renderInline(cfg.intro)}</p>}
        {data && upcoming.length === 0 && (
          <p className="text-base text-[#5C5C5C] rounded-2xl border border-dashed border-[#E2E0DC] px-5 py-6 text-center" data-testid="artist-shows-empty">{cfg.empty_text || "No dates posted yet — check back soon."}</p>
        )}
        <ul className="grid gap-3 sm:grid-cols-2" data-testid="artist-shows-list">
          {upcoming.map((s, i) => (
            <li key={`${s.date}-${i}`} className="flex gap-3 rounded-2xl border border-[#E2E0DC] bg-white p-3" data-testid={`artist-show-${i}`}>
              {s.flyer_url && <img src={s.flyer_url} alt="" className="h-20 w-16 rounded-lg object-cover shrink-0" loading="lazy" />}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-extrabold uppercase tracking-wide" style={{ color: accent }}>{when(s.date, s.time)}</div>
                <div className="font-bold text-[#1A1A1A] leading-tight mt-0.5">{s.title || s.note || s.venue_name || "TBA"}</div>
                <div className="text-sm text-[#5C5C5C]">{[(s.title || s.note) ? s.venue_name : null, s.city, s.state].filter(Boolean).join(", ")}</div>
                {s.residency && <div className="text-[11px] font-bold mt-0.5" style={{ color: accent }}>Every {s.residency.weekday} · {s.residency.count} dates through {s.residency.until}</div>}
                {s.ext_link && <a href={s.ext_link} target="_blank" rel="noopener noreferrer" className="text-xs font-bold underline" style={{ color: accent }}>Tickets / info →</a>}
              </div>
            </li>
          ))}
        </ul>
        {past.length > 0 && (
          <div className="mt-4 text-xs text-[#9A9A9A]" data-testid="artist-shows-past">Recently: {past.map((s) => `${s.venue_name || "show"} (${s.date})`).join(" · ")}</div>
        )}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {cfg.artist_slug && <AddToCalendar slug={cfg.artist_slug} />}
          {cfg.full_link && <Link to={cfg.full_link} className="text-sm font-bold text-[#1A1A1A] underline-offset-2 hover:underline" data-testid="artist-shows-full-link">{cfg.full_link_label || "Full tour & map →"}</Link>}
        </div>
        <div className="mt-4"><OwnerShowsCta slug={cfg.artist_slug} accent={accent} /></div>
      </BlockShell>
    </section>
  );
}
